import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { notify } from '../../helper/notify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker, { registerLocale } from 'react-datepicker';
import sv from 'date-fns/locale/sv';
import { TableHeader, TableColumn, TableBody, TableRow, TableCell } from '../dashboard/table-components';
import { PillTabs } from '../dashboard/pill-tabs.component';
import { getEmployeeCertificates, getDraftEmployeeCertificates, deleteCertificate, deleteDraftCertificate } from '../../features/employer-certificate/employer-certificate.slice';
import { downloadCertificateApi } from '../../features/employer-certificate/employer-certificate.service';
import './certificates-table.component.scss';

/* Register the Swedish locale with react-datepicker once at module
   load — same pattern as the Employment Details picker. The library
   is built on date-fns and uses its locale system to translate month
   names, weekday headers, screen-reader strings. After this, passing
   `locale="sv"` on the <DatePicker> swaps every label to Swedish. */
registerLocale('sv', sv);

/* Portal the calendar popper out of the DOM tree so it's never clipped
   by an ancestor's `overflow: hidden` (rounded card corners) or a
   `transform`-based containing block (entrance animations on this
   page). Rendering directly under <body> means the calendar always
   has the whole viewport available. */
const PopperPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
};

/* String <-> Date bridges. The parent code stores `dateFrom` / `dateTo`
   as `yyyy-mm-dd` ISO strings; the API also expects that format. The
   picker speaks Date | null, so we convert at the boundary and keep
   every other consumer (URL params, API payloads, Redux) unchanged. */
const parseIso = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

const toIso = (d: Date | null): string => {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

interface Certificate {
    contractNo: string;
    duration: string;
    workHoursRecorded: string;
    issuedDate: string;
}

interface CertificatesTableProps {
    onOrderCertificate: () => void;
}

export const CertificatesTable: React.FC<CertificatesTableProps> = ({
    onOrderCertificate
}) => {
    const { t, i18n } = useTranslation();
    /* Re-evaluates whenever i18next switches language; the picker
       re-renders with Swedish month/weekday labels when the user
       flips the language toggle, no remount needed. */
    const calendarLocale = (i18n.language || '').toLowerCase().startsWith('sv') ? 'sv' : undefined;
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [certificateType, setCertificateType] = useState<'employer' | 'draft'>('employer');
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Tabs for the certificate-type switcher (replaces the old dropdown).
    // Source of truth is still `certificateType` so every downstream effect /
    // selector / API dispatch keeps working unchanged.
    const certificateTypeTabs = [
        { key: 'employer', label: t('Employer Certificate') },
        { key: 'draft',    label: t('Draft Employer Certificate') },
    ];

    const employeeCertificates = useSelector((state: any) => state.employerCertificate.employeeCertificates);
    const draftEmployeeCertificates = useSelector((state: any) => state.employerCertificate.draftEmployeeCertificates);
    const isLoading = useSelector((state: any) => 
        certificateType === 'employer' 
            ? state.employerCertificate.certificatesLoading 
            : state.employerCertificate.draftCertificatesLoading
    );
    const isDeleting = useSelector((state: any) => state.employerCertificate.deleting);

    const certificateData = certificateType === 'employer' ? employeeCertificates : draftEmployeeCertificates;

    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, [searchQuery]);

    useEffect(() => {
        const params: any = {};
        if (dateFrom) params.fromDate = dateFrom;
        if (dateTo) params.toDate = dateTo;
        if (debouncedSearch) params.searchQuery = debouncedSearch;
        const finalParams = Object.keys(params).length ? params : undefined;
        
        if (certificateType === 'employer') {
            dispatch(getEmployeeCertificates(finalParams));
        } else {
            dispatch(getDraftEmployeeCertificates(finalParams));
        }
    }, [dispatch, dateFrom, dateTo, debouncedSearch, certificateType]);

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return t('Present');
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        /* yyyy-mm-dd — universal ISO-8601 format (zero-padded month + day).
           Drives every displayed date in this section: the "Duration"
           column's from / to (employment period), and the "Issued date"
           column. */
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const certificates: Certificate[] = certificateData.map((cert: any) => {
        const totalHours = (cert.employeeWagesDetails || []).reduce(
            (sum: number, w: any) => sum + (w.hoursworked || 0), 0
        );
        const from = formatDate(cert.employmentPeriodFrom);
        const to = cert.isStillEmployed ? t('Present') : formatDate(cert.employmentPeriodEnd);
        return {
            contractNo: String(cert.certificateId),
            duration: `${from} - ${to}`,
            workHoursRecorded: `${totalHours}h`,
            issuedDate: formatDate(cert.createdDate),
        };
    });

    const filteredCertificates = certificates;

    const handleEdit = (contractNo: string) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('certificateId', contractNo);
        if (certificateType === 'draft') {
            searchParams.set('isEmployerCertificateDraft', 'true');
        } else {
            searchParams.delete('isEmployerCertificateDraft');
        }
        navigate(`${location.pathname}?${searchParams.toString()}`);
    };

    const handleDownload = (contractNo: string) => {
        downloadCertificateApi(Number(contractNo)).catch((err) => {
            console.error('Certificate download failed:', err);
        });
    };

    const handleDelete = (contractNo: string) => {
        const cert = certificateData.find((c: any) => String(c.certificateId) === contractNo);
        if (cert) setDeleteConfirmId(cert.certificateId);
    };

    const handleConfirmDelete = () => {
        if (deleteConfirmId !== null) {
            const action = certificateType === 'draft'
                ? deleteDraftCertificate(deleteConfirmId)
                : deleteCertificate(deleteConfirmId);
            dispatch(action).then((result: any) => {
                setDeleteConfirmId(null);
                if (result.meta?.requestStatus === 'fulfilled') {
                    const msg: string = String(t('employerCertificateDeletedSuccessfully'));
                    notify.success(msg);
                } else {
                    const msg: string = String(result?.payload || t('somethingWentWrong'));
                    notify.error(msg);
                }
            });
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmId(null);
    };

    return (
        <>
            {/* Certificate-type switcher — sliding violet pill tabs.
                Lives ABOVE the "Your certificates" section card so the choice
                of view (Employer vs Draft) reads as a page-level filter
                rather than just another control crammed inside the table
                header. Source of truth is still `certificateType`, so every
                downstream effect/selector/dispatch keeps working unchanged. */}
            <div className="certificates-table__type-tabs-row">
                <PillTabs
                    tabs={certificateTypeTabs}
                    activeTab={certificateType}
                    onTabChange={(key) => setCertificateType(key as 'employer' | 'draft')}
                    ariaLabel={t('Certificate type')}
                />
            </div>

            <div className="certificates-table">
                <div className="certificates-table__header">
                    <h2 className="certificates-table__title">{t('Your certificates')}</h2>
                    <div className="certificates-table__controls">
                        <div className="certificates-table__search">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#49454F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M14 14L11.1 11.1" stroke="#49454F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <input
                            type="text"
                            placeholder={t('Search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="certificates-table__search-input"
                        />
                    </div>

                    <div className="certificates-table__date-filters">
                        <div className="certificates-table__date-field">
                            <DatePicker
                                selected={parseIso(dateFrom)}
                                onChange={(d: Date | null) => setDateFrom(toIso(d))}
                                dateFormat="yyyy-MM-dd"
                                placeholderText={t('Date from')}
                                className="certificates-table__date-input"
                                wrapperClassName="certificates-table__date-input-dp"
                                popperContainer={PopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="certificates-table__date-dp-popper"
                                calendarClassName="certificates-table__date-dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                locale={calendarLocale}
                                autoComplete="off"
                                isClearable={!!dateFrom}
                            />
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 17 19" fill="none">
                                <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>

                        <div className="certificates-table__date-field">
                            <DatePicker
                                selected={parseIso(dateTo)}
                                onChange={(d: Date | null) => setDateTo(toIso(d))}
                                dateFormat="yyyy-MM-dd"
                                placeholderText={t('Date to')}
                                className="certificates-table__date-input"
                                wrapperClassName="certificates-table__date-input-dp"
                                popperContainer={PopperPortal}
                                popperProps={{ strategy: 'fixed' }}
                                popperClassName="certificates-table__date-dp-popper"
                                calendarClassName="certificates-table__date-dp-calendar"
                                onChangeRaw={(e: React.SyntheticEvent | undefined) => e?.preventDefault()}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="scroll"
                                locale={calendarLocale}
                                autoComplete="off"
                                minDate={parseIso(dateFrom) || undefined}
                                isClearable={!!dateTo}
                            />
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 17 19" fill="none">
                                <path d="M1 7.66667H16M5.16667 4.33333V1M11.8333 4.33333V1M6.33333 17.6667H10.6667C12.5335 17.6667 13.4669 17.6667 14.18 17.3034C14.8072 16.9838 15.3171 16.4738 15.6367 15.8466C16 15.1336 16 14.2002 16 12.3333V8C16 6.13316 16 5.19974 15.6367 4.4867C15.3171 3.85949 14.8072 3.34956 14.18 3.02998C13.4669 2.66667 12.5335 2.66667 10.6667 2.66667H6.33333C4.46649 2.66667 3.53307 2.66667 2.82003 3.02998C2.19282 3.34956 1.68289 3.85949 1.36331 4.4867C1 5.19974 1 6.13316 1 8V12.3333C1 14.2002 1 15.1336 1.36331 15.8466C1.68289 16.4738 2.19282 16.9838 2.82003 17.3034C3.53307 17.6667 4.46649 17.6667 6.33333 17.6667Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                    </div>

                    <button 
                        className="certificates-table__order-btn"
                        onClick={onOrderCertificate}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                            <path d="M6.33333 1V11.6667M11.6667 6.33333L1 6.33333" stroke="#544196" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        {t('Order Certificate')}
                    </button>
                </div>
            </div>

            <div className="certificates-table__content">
                <div className="certificates-table__data-table">
                    <TableHeader>
                        <TableColumn>{t('Contract No.')}</TableColumn>
                        <TableColumn>{t('Duration')}</TableColumn>
                        <TableColumn>{t('Work Hours Recorded')}</TableColumn>
                        <TableColumn>{t('Issued Date')}</TableColumn>
                        <TableColumn flex={0.5}>{t('Action')}</TableColumn>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            [0, 1, 2].map((i) => (
                                <TableRow key={i} className="certificates-table__skeleton-row">
                                    {[0, 1, 2, 3, 4].map((j) => (
                                        <TableCell key={j} flex={j === 4 ? 0.5 : undefined}>
                                            <span className="certificates-table__skeleton-cell" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : filteredCertificates.length === 0 ? (
                            <div className="certificates-table__empty">
                                <span>{t('No data found')}</span>
                            </div>
                        ) : (
                            filteredCertificates.map((cert) => (
                                <TableRow key={cert.contractNo}>
                                    <TableCell> 
                                        <span className="certificates-table__contract-link">
                                            {cert.contractNo}
                                        </span>
                                    </TableCell>
                                    <TableCell>{cert.duration}</TableCell>
                                    <TableCell>{cert.workHoursRecorded}</TableCell>
                                    <TableCell>{cert.issuedDate}</TableCell>
                                    <TableCell flex={0.5}>
                                        <div className="certificates-table__actions">
                                            <button
                                                className="certificates-table__action-btn certificates-table__action-btn--edit"
                                                onClick={() => handleEdit(cert.contractNo)}
                                                aria-label={t('Edit certificate', { defaultValue: 'Edit certificate' })}
                                                data-tip={t('Edit certificate', { defaultValue: 'Edit certificate' })}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path d="M0 9.50167V12.0017H2.5L9.87333 4.62833L7.37333 2.12833L0 9.50167ZM11.8067 2.695C12.0667 2.435 12.0667 2.015 11.8067 1.755L10.2467 0.195C9.98667 -0.065 9.56667 -0.065 9.30667 0.195L8.08667 1.415L10.5867 3.915L11.8067 2.695V2.695Z" fill="#6750B8"/>
                                                </svg>
                                            </button>
                                            {certificateType !== 'draft' && (
                                            <button
                                                className="certificates-table__action-btn certificates-table__action-btn--download"
                                                onClick={() => handleDownload(cert.contractNo)}
                                                aria-label={t('Download PDF', { defaultValue: 'Download PDF' })}
                                                data-tip={t('Download PDF', { defaultValue: 'Download PDF' })}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                    <path d="M8 1C8 0.447715 7.55228 0 7 0C6.44772 5.96046e-08 6 0.447715 6 1L7 1L8 1ZM6 8.33333C6 8.88562 6.44772 9.33333 7 9.33333C7.55229 9.33333 8 8.88562 8 8.33333H7H6ZM10.3738 7.70711C10.7643 7.31658 10.7643 6.68342 10.3738 6.29289C9.98325 5.90237 9.35008 5.90237 8.95956 6.29289L9.66667 7L10.3738 7.70711ZM7.4714 9.19526L6.7643 8.48816V8.48816L7.4714 9.19526ZM6.5286 9.19526L7.2357 8.48816L7.2357 8.48816L6.5286 9.19526ZM5.04044 6.29289C4.64992 5.90237 4.01675 5.90237 3.62623 6.29289C3.2357 6.68342 3.2357 7.31658 3.62623 7.70711L4.33333 7L5.04044 6.29289ZM2 9.66667C2 9.11438 1.55228 8.66667 1 8.66667C0.447715 8.66667 0 9.11438 0 9.66667H1H2ZM14 9.66667C14 9.11438 13.5523 8.66667 13 8.66667C12.4477 8.66667 12 9.11438 12 9.66667H13H14ZM11.908 12.782L11.454 11.891H11.454L11.908 12.782ZM12.782 11.908L13.673 12.362V12.362L12.782 11.908ZM1.21799 11.908L0.32698 12.362L1.21799 11.908ZM2.09202 12.782L1.63803 13.673L1.63803 13.673L2.09202 12.782ZM7 1L6 1L6 8.33333H7H8L8 1L7 1ZM9.66667 7L8.95956 6.29289L6.7643 8.48816L7.4714 9.19526L8.17851 9.90237L10.3738 7.70711L9.66667 7ZM6.5286 9.19526L7.2357 8.48816L5.04044 6.29289L4.33333 7L3.62623 7.70711L5.82149 9.90237L6.5286 9.19526ZM7.4714 9.19526L6.7643 8.48816C6.89447 8.35798 7.10553 8.35798 7.2357 8.48816L6.5286 9.19526L5.82149 9.90237C6.47236 10.5532 7.52764 10.5532 8.17851 9.90237L7.4714 9.19526ZM1 9.66667H0V9.8H1H2V9.66667H1ZM4.2 13V14H9.8V13V12H4.2V13ZM13 9.8H14V9.66667H13H12V9.8H13ZM9.8 13V14C10.3436 14 10.8114 14.0008 11.195 13.9694C11.5904 13.9371 11.9836 13.8658 12.362 13.673L11.908 12.782L11.454 11.891C11.4045 11.9162 11.3038 11.9539 11.0322 11.9761C10.7488 11.9992 10.3766 12 9.8 12V13ZM13 9.8H12C12 10.3766 11.9992 10.7488 11.9761 11.0322C11.9539 11.3038 11.9162 11.4045 11.891 11.454L12.782 11.908L13.673 12.362C13.8658 11.9836 13.9371 11.5904 13.9694 11.195C14.0008 10.8114 14 10.3436 14 9.8H13ZM11.908 12.782L12.362 13.673C12.9265 13.3854 13.3854 12.9265 13.673 12.362L12.782 11.908L11.891 11.454C11.7951 11.6422 11.6422 11.7951 11.454 11.891L11.908 12.782ZM1 9.8H0C0 10.3436 -0.000777721 10.8114 0.0305694 11.195C0.0628704 11.5904 0.134186 11.9836 0.32698 12.362L1.21799 11.908L2.10899 11.454C2.0838 11.4045 2.04612 11.3038 2.02393 11.0322C2.00078 10.7488 2 10.3766 2 9.8H1ZM4.2 13V12C3.62345 12 3.25117 11.9992 2.96784 11.9761C2.69617 11.9539 2.59545 11.9162 2.54601 11.891L2.09202 12.782L1.63803 13.673C2.01641 13.8658 2.40963 13.9371 2.80497 13.9694C3.18864 14.0008 3.65645 14 4.2 14V13ZM1.21799 11.908L0.32698 12.362C0.614601 12.9265 1.07354 13.3854 1.63803 13.673L2.09202 12.782L2.54601 11.891C2.35785 11.7951 2.20487 11.6422 2.10899 11.454L1.21799 11.908Z" fill="#0A77FF"/>
                                                </svg>
                                            </button>
                                            )}
                                            <button
                                                className="certificates-table__action-btn certificates-table__action-btn--delete"
                                                onClick={() => handleDelete(cert.contractNo)}
                                                aria-label={t('Delete certificate', { defaultValue: 'Delete certificate' })}
                                                data-tip={t('Delete certificate', { defaultValue: 'Delete certificate' })}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="16" viewBox="0 0 13 16" fill="none">
                                                    <path d="M1 2.66667C0.447715 2.66667 0 3.11438 0 3.66667C0 4.21895 0.447715 4.66667 1 4.66667V3.66667V2.66667ZM11.6667 4.66667C12.219 4.66667 12.6667 4.21895 12.6667 3.66667C12.6667 3.11438 12.219 2.66667 11.6667 2.66667V3.66667V4.66667ZM2.66667 3.66667C2.66667 4.21895 3.11438 4.66667 3.66667 4.66667C4.21895 4.66667 4.66667 4.21895 4.66667 3.66667H3.66667H2.66667ZM8 3.66667C8 4.21895 8.44772 4.66667 9 4.66667C9.55229 4.66667 10 4.21895 10 3.66667H9H8ZM11 3.66667L10 3.65774L9.9402 10.3601L10.9402 10.369L11.9401 10.378L12 3.67559L11 3.66667ZM6.94032 14.3333V13.3333H5.66667V14.3333V15.3333H6.94032V14.3333ZM1.66667 3.66667H0.666667V10.3333H1.66667H2.66667V3.66667H1.66667ZM1 3.66667V4.66667H1.66667V3.66667V2.66667H1V3.66667ZM1.66667 3.66667V4.66667H11V3.66667V2.66667H1.66667V3.66667ZM11 3.66667V4.66667H11.6667V3.66667V2.66667H11V3.66667ZM3.66667 3.37037H4.66667C4.66667 2.72101 5.29888 2 6.33333 2V1V0C4.42227 0 2.66667 1.40149 2.66667 3.37037H3.66667ZM6.33333 1V2C7.36779 2 8 2.72101 8 3.37037H9H10C10 1.40149 8.2444 0 6.33333 0V1ZM3.66667 3.37037H2.66667V3.66667H3.66667H4.66667V3.37037H3.66667ZM9 3.37037H8V3.66667H9H10V3.37037H9ZM5.66667 14.3333V13.3333C4.00981 13.3333 2.66667 11.9902 2.66667 10.3333H1.66667H0.666667C0.666667 13.0948 2.90524 15.3333 5.66667 15.3333V14.3333ZM10.9402 10.369L9.9402 10.3601C9.9255 12.0065 8.58672 13.3333 6.94032 13.3333V14.3333V15.3333C9.68433 15.3333 11.9156 13.1219 11.9401 10.378L10.9402 10.369Z" fill="#E33B32"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </div>

                <div className="certificates-table__mobile-list">
                    {isLoading ? (
                        [0, 1, 2].map((i) => (
                            <div key={i} className="certificates-table__mobile-card certificates-table__mobile-card--skeleton">
                                {[0, 1, 2, 3].map((j) => (
                                    <span key={j} className="certificates-table__skeleton-cell" />
                                ))}
                            </div>
                        ))
                    ) : filteredCertificates.length === 0 ? (
                        <div className="certificates-table__empty">
                            <span>{t('No data found')}</span>
                        </div>
                    ) : (
                        filteredCertificates.map((cert) => (
                            <div key={cert.contractNo} className="certificates-table__mobile-card">
                                <div className="certificates-table__mobile-row">
                                    <span className="certificates-table__mobile-label">{t('Contract No.')}</span>
                                    <span className="certificates-table__contract-link">{cert.contractNo}</span>
                                </div>
                                <div className="certificates-table__mobile-row">
                                    <span className="certificates-table__mobile-label">{t('Duration')}</span>
                                    <span>{cert.duration}</span>
                                </div>
                                <div className="certificates-table__mobile-row">
                                    <span className="certificates-table__mobile-label">{t('Work Hours Recorded')}</span>
                                    <span>{cert.workHoursRecorded}</span>
                                </div>
                                <div className="certificates-table__mobile-row">
                                    <span className="certificates-table__mobile-label">{t('Issued Date')}</span>
                                    <span>{cert.issuedDate}</span>
                                </div>
                                <div className="certificates-table__mobile-actions">
                                    <button
                                        onClick={() => handleEdit(cert.contractNo)}
                                        title={t('Edit')}
                                        className="certificates-table__mobile-action-btn"
                                    >
                                        {t('Edit')}
                                    </button>
                                    {certificateType !== 'draft' && (
                                    <button
                                        onClick={() => handleDownload(cert.contractNo)}
                                        title={t('Download')}
                                        className="certificates-table__mobile-action-btn"
                                    >
                                        {t('Download')}
                                    </button>
                                    )}
                                    <button
                                        onClick={() => handleDelete(cert.contractNo)}
                                        title={t('Delete')}
                                        className="certificates-table__mobile-action-btn certificates-table__mobile-action-btn--danger"
                                    >
                                        {t('Delete')}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Delete-confirmation modal — portaled into <body> so the
                backdrop's `position: fixed` centers on the viewport instead
                of on whichever ancestor happens to have a `transform` set
                (`.ec-reveal` runs a translateY entrance animation, which
                turns fixed-positioned descendants into "fixed relative to
                the transformed ancestor" and pinned the modal to the
                table area). Portaling escapes every containing-block
                quirk in one shot. Click-on-backdrop and Esc dismiss are
                honoured via `handleCancelDelete`, gated by isDeleting. */}
            {deleteConfirmId !== null && ReactDOM.createPortal(
                <div
                    className="certificates-table__modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="certificates-table-modal-title"
                    onClick={(e) => {
                        if (e.target === e.currentTarget && !isDeleting) handleCancelDelete();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape' && !isDeleting) handleCancelDelete();
                    }}
                    tabIndex={-1}
                >
                    <div className="certificates-table__modal">
                        {/* Destructive-action alert icon — red-tinted
                            triangle inside a soft red halo. Same visual
                            language as the ConfirmDeleteModal so both
                            delete flows in the app feel consistent. */}
                        <div className="certificates-table__modal-icon" aria-hidden="true">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 3.75l9.5 16.5H2.5L12 3.75z"
                                    fill="rgba(239, 68, 68, 0.12)"
                                    stroke="#ef4444"
                                    strokeWidth="1.8"
                                    strokeLinejoin="round"
                                />
                                <path d="M12 10v4" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" />
                                <circle cx="12" cy="17" r="1.1" fill="#ef4444" />
                            </svg>
                        </div>
                        <h3 id="certificates-table-modal-title" className="certificates-table__modal-title">{t('Delete Certificate')}</h3>
                        <p className="certificates-table__modal-body">
                            {certificateType === 'draft'
                                ? t('Are you sure you want to delete this Draft Employer Certificate?')
                                : t('Are you sure you want to delete this certificate?')}
                        </p>
                        <p className="certificates-table__modal-body certificates-table__modal-body--warning">{t('This action cannot be undone.')}</p>
                        <div className="certificates-table__modal-actions">
                            <button
                                className="certificates-table__modal-btn certificates-table__modal-btn--cancel"
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                {t('Cancel')}
                            </button>
                            <button
                                className="certificates-table__modal-btn certificates-table__modal-btn--confirm"
                                onClick={handleConfirmDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <span className="certificates-table__modal-spinner" />
                                ) : (
                                    t('Yes, Delete')
                                )}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
        </>
    );
};
